/**
 * The browser half of the person tier — the chooser's read and the switcher's
 * write.
 *
 * Authority: IMPL-ENGAGE-002 §8.2–§8.3; `apps/api/src/functions/me.engagements.ts`
 * (the API half this wraps); `@nimer/contracts/person` for the wire shapes
 * both sides already agree on.
 *
 * Thin and pure, on the same discipline as `customer-otp.ts`: no React, no
 * state, no retry. Neither call sends a credential this file holds — the
 * cookie is `HttpOnly` and the browser attaches it — and neither ever sends an
 * address, because the API takes the address from the session. A parameterised
 * address here would be the enumeration oracle §8.2 exists to prevent, so
 * there is deliberately no parameter to pass one through.
 *
 * `customer_ref` is not a credential either (§8.3). An unreachable one is a
 * 404 the caller falls back to the chooser on, never an error to render.
 */
import { ErrorEnvelopeSchema } from '@nimer/contracts/error'
import { ReachableEngagementsSchema, type ReachableEngagement } from '@nimer/contracts/person'

export const ME_ENGAGEMENTS_PATH = '/api/v1/me/engagements'
export const ME_SESSION_PATH = '/api/v1/me/session'
/** The flag is raised against the session's CURRENT engagement (§9.2). */
export const DUPLICATE_FLAG_PATH = '/api/v1/engagements/self/duplicate-flag'

/** A non-2xx from either endpoint, carrying the status a caller can branch on. */
export class PersonRequestFailed extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'PersonRequestFailed'
    this.status = status
  }
}

function messageFrom(body: unknown): string {
  const envelope = ErrorEnvelopeSchema.safeParse(body)

  return envelope.success ? envelope.data.error.message : 'request failed'
}

/**
 * Every live engagement the signed-in address reaches.
 *
 * Works under a selection token and under a full session alike, which is what
 * lets one function serve both the post-verify chooser and the persistent
 * switcher (§8.3: "the switcher is persistent chrome, not a sign-in gate").
 */
export async function fetchReachableEngagements(
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<readonly ReachableEngagement[]> {
  const response = await fetchImpl(ME_ENGAGEMENTS_PATH, { credentials: 'same-origin' })
  const parsed: unknown = await response.json().catch(() => null)
  if (!response.ok) throw new PersonRequestFailed(response.status, messageFrom(parsed))

  return ReachableEngagementsSchema.parse(parsed)
}

/**
 * Bind the session to one engagement.
 *
 * The new cookie arrives as `Set-Cookie` and is never in the body, so there is
 * nothing to return but the fact that it worked. `false` means the reference
 * was not reachable — the caller shows the chooser rather than an error, per
 * §8.3's "preselect, never dead-end".
 */
/**
 * "This looks like a duplicate of that one" (§9.2, audit F-05).
 *
 * ┌─ THE ONE AFFORDANCE THAT TREATS A ROW AS A PROBLEM, AND IT DID NOT EXIST ┐
 * │ §8.1: multiplicity is normal, duplication is the pathology. Acme's       │
 * │ pentest and Acme's vCISO retainer are two engagements, correctly — so    │
 * │ the chooser deliberately carries no warning styling and no count. It     │
 * │ names exactly one exception, "the explicit looks-like-a-duplicate flag,  │
 * │ which the person raises deliberately", and the endpoint for it has been  │
 * │ registered and tested with nothing on any screen to raise it.            │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * The flag is a statement, not a request: a consultant picks the survivor
 * (§9.4). Flagging twice returns the same answer as flagging once, so the
 * caller never learns whether a colleague got there first.
 */
export async function flagDuplicate(
  otherRef: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<boolean> {
  const response = await fetchImpl(DUPLICATE_FLAG_PATH, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ other_ref: otherRef }),
  })

  return response.ok
}

const selectionListeners = new Set<() => void>()

/**
 * Told when a session becomes bound to a deliberately chosen engagement.
 *
 * `CustomerAuthProvider` holds the sign-in state and the five screens that
 * call `selectEngagement` do not, so the fact has to travel somehow. It goes
 * through the ONE function that actually binds a session rather than being
 * dispatched from each caller, because a caller that forgets is a caller whose
 * owner stays gated on the chooser forever — which is the bug this exists to
 * stop, reintroduced one screen at a time.
 */
export function onEngagementSelected(listener: () => void): () => void {
  selectionListeners.add(listener)

  return () => {
    selectionListeners.delete(listener)
  }
}

export async function selectEngagement(
  customerRef: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<boolean> {
  const response = await fetchImpl(ME_SESSION_PATH, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ customer_ref: customerRef }),
  })
  if (response.status === 404) return false
  if (!response.ok) {
    const parsed: unknown = await response.json().catch(() => null)
    throw new PersonRequestFailed(response.status, messageFrom(parsed))
  }

  // Only on the success path: a refused reference has bound nothing, and
  // announcing a selection that did not happen would ungate the chooser while
  // the session still points at an engagement the person did not pick.
  for (const listener of selectionListeners) listener()

  return true
}
